import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
    assertUnreachable,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigService,
    DeviceIdService,
    isDefined,
    ModalClass,
    NOTIFICATION_STATUS_CODE,
    NotificationIdentifier,
    NotificationInterface,
    YesNoModalDialogComponent,
    YesNoModalDialogModel,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import {
    faArrowLeft,
    faArrowRight,
    faCog,
    faEllipsisV,
    faFileSignature,
    faFileUpload,
    faFilter,
    faMinusCircle,
    faQuestion,
    faRedo,
    faSave,
    faSitemap,
    faTasks,
    faTrashAlt,
    faUserFriends,
    faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { combineLatest, fromEvent, Observable, of, Subject } from 'rxjs';
import {
    concatMap,
    delay,
    exhaustMap,
    filter,
    first,
    map,
    mergeMap,
    startWith,
    switchMap,
    take,
    takeUntil,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import { NotificationInstanceToken } from '../../core/interfaces/notification.interface';
import { FormsStorageService } from '../../services/forms-storage.service';
import {
    abortForm,
    clearFilters,
    collapseAccordionByGroup,
    enableManualIndividualization,
    loadForm,
    removeParticipantForm,
    saveForm,
    selectNextParticipant,
    selectPreviousParticipant,
    submitForm,
    updateFormReadOnlyReason,
} from '../../store/actions/form.actions';
import {
    getAccordionGroups,
    getDeviceId,
    getForm,
    getFormGlobalReadonly,
    getFormInfoClass,
    getFormIsSaving,
    getFormLock,
    getFormNotification,
    getFormParticipantCopies,
    getFormParticipantCopyReadonly,
    getFormReadonlyReason,
    getFormSelectedParticipantCopyId,
    getFormSubmitStatusForCurrentParticipant,
    getFormSupportParticipantCopies,
    getFormSystemCondition,
    getFormType,
    getFormUnSubmittedParticipantCopyIds,
    getInstallationForm,
    getIsAnyFilterActive,
    getManualIndividualizationStatus,
    getQuestionnaireSubmitStatus,
    getSignaturesValid,
} from '../../store/selectors/form.selector';
import { FormState } from '../../store/state/form.state';
import { initialNotificationIdentifier } from '../../store/state/notification-identifier.state';
import { FormParticipantCopies } from '../../types/form-participant-copies';
import { FormsConstants } from '../../types/forms-constants';
import { ParticipantCopyId } from '../../types/participant-copy';
import { SystemCondition } from '../../types/system-condition';
import { FormUtilities } from '../../utilities/form-utilities';
import { NotificationUtilities } from '../../utilities/notification-utilities';
import { SubmitStatusUtilities } from '../../utilities/submit-status-utilities';
import { ParticipantCopiesComponent } from '../participant-copies/participant-copies.component';
import { RecipientsComponent } from '../recipients/recipients.component';
import { DocumentFeedbackModel } from '../document-feedback/document-feedback.model';
import { DocumentFeedbackComponent } from '../document-feedback/document-feedback.component';
import { BroadcastService } from '../../services/broadcast.service';
import { FormStatusMessage } from '../../types/broadcast';
import { getPreviousSubmitFromFormListEntry } from '../../store/selectors/list.selector';

// exporting function to make it testable
export function getHelpMessage(
    translate: TranslateService,
    form: FormState,
    notification: NotificationIdentifier,
    readOnlyReason: FormsConstants.READONLY_REASON,
    participantCopyReadOnly: boolean,
    submitMode: FormsConstants.SUBMIT_STATUS,
    canHandOver: boolean,
    canSubmit: boolean,
    canDelete: boolean,
    canAbort: boolean,
    supportParticipantCopies: boolean,
    systemCondition: SystemCondition,
    isInstallationReport: boolean,
    isServicePartQuestionnaire: boolean,
    currentDeviceHasLock: boolean,
    currentUserHasLock: boolean,
    isSafetyAssessmentForm: boolean,
): { title: string; body: string } {
    let title = '';
    const bodyItems: string[] = [];

    // Explain read-only state
    if (readOnlyReason !== FormsConstants.READONLY_REASON.NONE) {
        title = <string>translate.instant('F4M_ACTIONBAR_INFO_READONLY_TITLE');
        switch (readOnlyReason) {
            case FormsConstants.READONLY_REASON.FINAL:
                bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_READONLY_BODY_FINAL'));
                break;
            case FormsConstants.READONLY_REASON.INTERIM:
                bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_READONLY_BODY_INTERIM'));
                break;
            case FormsConstants.READONLY_REASON.HANDOVER:
                bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_READONLY_BODY_HANDOVER'));
                break;
            case FormsConstants.READONLY_REASON.OSR:
                bodyItems.push(
                    <string>translate.instant('F4M_ACTIONBAR_INFO_READONLY_BODY_OSR', {
                        notification: notification.qmNum,
                    }),
                );
                break;
            case FormsConstants.READONLY_REASON.PREVIEW:
                bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_READONLY_BODY_PREVIEW'));
                break;
            case FormsConstants.READONLY_REASON.LOCK:
                if (!currentUserHasLock) {
                    bodyItems.push(
                        <string>translate.instant('F4M_ACTIONBAR_INFO_READONLY_BODY_LOCK', {
                            lockUser: form.lockedBy,
                        }),
                    );
                }
                if (!currentDeviceHasLock) {
                    bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_READONLY_BODY_DEVICE_LOCK'));
                }
                break;
            case FormsConstants.READONLY_REASON.NOT_LOCKED:
                bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_READONLY_BODY_NOT_LOCKED'));
                break;
            case FormsConstants.READONLY_REASON.PREVIOUS_SUBMIT:
                bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_READONLY_BODY_PREVIOUS_SUBMIT'));
                break;
            case FormsConstants.READONLY_REASON.REFRESHED_FORM_OLD_VERSION:
                bodyItems.push(
                    <string>translate.instant('F4M_ACTIONBAR_INFO_READONLY_BODY_REFRESHED_FORM_OLD_VERSION'),
                );
                break;
            case FormsConstants.READONLY_REASON.MANUAL_INDIVIDUALIZATION:
                bodyItems.push(
                    <string>translate.instant('F4M_ACTIONBAR_INFO_READONLY_MANUAL_INDIVIDUALIZATION_ENABLED'),
                );
                break;
            case FormsConstants.READONLY_REASON.SUBMITTED_FROM_ANOTHER_TAB:
                bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_READONLY_SUBMITTED_FROM_ANOTHER_TAB'));
                break;
            case FormsConstants.READONLY_REASON.ABORTED_FROM_ANOTHER_TAB:
                bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_READONLY_ABORTED_FROM_ANOTHER_TAB'));
                break;
            case FormsConstants.READONLY_REASON.HANDOVER_FROM_ANOTHER_TAB:
                bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_READONLY_HANDOVER_FROM_ANOTHER_TAB'));
                break;
            default:
                assertUnreachable(readOnlyReason);
        }
    } else if (
        // alternative explanation for read-only state when participant copies are supported (which is not covered by global readonly-reason)
        participantCopyReadOnly
    ) {
        title = <string>translate.instant('F4M_ACTIONBAR_INFO_READONLY_TITLE');
        bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_READONLY_COPY_SUBMITTED'));
    } else {
        // form is editable
        title = <string>translate.instant('F4M_ACTIONBAR_INFO_TITLE');
        bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_BODY'));

        // Explain submit state
        if (canSubmit) {
            if (supportParticipantCopies) {
                if (form.selectedParticipantCopyId === FormsConstants.MASTER_PARTICIPANT_ID) {
                    bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_BULK_SUBMIT_FINAL'));
                } else {
                    bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_SUBMIT_COPY'));
                }
            } else {
                // canSubmit will only be true if SUBMIT_STATUS is either INTERIM or FINAL
                switch (submitMode) {
                    case FormsConstants.SUBMIT_STATUS.INTERIM:
                        bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_SUBMIT_INTERIM'));
                        break;
                    case FormsConstants.SUBMIT_STATUS.INTERIM_NOT_OK:
                        if (isInstallationReport) {
                            bodyItems.push(
                                <string>translate.instant('F4M_ACTIONBAR_INFO_SUBMIT_INTERIM_INSTALLATION_NOT_OK'),
                            );
                        } else {
                            bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_SUBMIT_INTERIM_NOT_OK'));
                        }
                        break;
                    case FormsConstants.SUBMIT_STATUS.FINAL:
                        bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_SUBMIT_FINAL'));
                        break;
                    case FormsConstants.SUBMIT_STATUS.FINAL_NOT_OK:
                        bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_SUBMIT_FINAL_NOT_OK'));
                        break;
                    case FormsConstants.SUBMIT_STATUS.HANDOVER:
                    case FormsConstants.SUBMIT_STATUS.EDITED:
                    case FormsConstants.SUBMIT_STATUS.UNKNOWN:
                    case FormsConstants.SUBMIT_STATUS.ABORTED:
                    case FormsConstants.SUBMIT_STATUS.REFRESHED:
                        throw new Error(`Form2NavbarComponent: helpTrigger$: Unexpected submit mode '${submitMode}'`);
                    default:
                        assertUnreachable(submitMode);
                }
            }
        } else {
            // Submit not possible; let's figure out why!
            if (isServicePartQuestionnaire) {
                bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_SERVICE_PART_QUESTIONNAIRE_SUBMIT'));
            } else if (supportParticipantCopies) {
                if (form.selectedParticipantCopyId === FormsConstants.MASTER_PARTICIPANT_ID) {
                    // check if there are copies
                    if (Object.values(form.participantCopies).length === 1) {
                        bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_NO_COPIES_AVAILABLE'));
                    } else if (
                        // Check if all participants have copies
                        Object.values(form.participantCopies).length !==
                        Object.values(form.participantCopies).filter((c) => c.hasCopy).length
                    ) {
                        bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_PARTICIPANT_WITHOUT_COPY'));
                    } else if (
                        // Check if there are copies without signatures
                        form.selectedParticipantCopyId === FormsConstants.MASTER_PARTICIPANT_ID &&
                        !SubmitStatusUtilities.hasSignedParticipantCopies(
                            form.selectedParticipantCopyId,
                            form.participantCopies,
                        )
                    ) {
                        bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_COPY_INCOMPLETE'));
                    }
                } else if (
                    // Check if this particular copy was signed
                    !SubmitStatusUtilities.isSignedParticipantCopy(
                        form.participantCopies[form.selectedParticipantCopyId],
                    )
                ) {
                    bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_COPY_INCOMPLETE'));
                } else {
                    throw new Error(
                        `Forms2NavbarComponent: getHelpMessage: Arrived at invalid state: Form is editable, but not submittable. Participant copy is selected and completed with signature. Should be submittable!`,
                    );
                }
            } else if (
                typeof systemCondition !== 'undefined' &&
                !SubmitStatusUtilities.systemConditionValid(systemCondition)
            ) {
                bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_SYSTEM_CONDITION_INVALID'));
            }
        }

        if (!isSafetyAssessmentForm) {
            if (canHandOver) {
                bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_SUBMIT_HANDOVER'));
            } else if (form.selectedParticipantCopyId !== FormsConstants.MASTER_PARTICIPANT_ID) {
                // Only the master checklist should be handed over. Participant copies cannot be handed over
                bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_SUBMIT_HANDOVER_PARTICIPANT_COPY'));
            } else if (isServicePartQuestionnaire) {
                bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_SERVICE_PART_QUESTIONNAIRE_HANDOVER'));
            } else {
                // If the Handover button is still disabled, then the only remaining reason is that there are un-submitted
                // participant copies
                bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_SUBMIT_HANDOVER_UNSUBMITTED_COPIES'));
            }

            // deleting a copy is only possible if participant copies are supported
            if (supportParticipantCopies) {
                if (canDelete) {
                    bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_DELETE'));
                } else if (form.selectedParticipantCopyId === FormsConstants.MASTER_PARTICIPANT_ID) {
                    // Only the master checklist should be handed over. Participant copies cannot be handed over
                    bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_DELETE_MASTER'));
                } else {
                    // If the Handover button is still disabled, then the only remaining reason is that there are un-submitted
                    // participant copies
                    bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_DELETE_ALREADY_SUBMITTED'));
                }
            }

            if (canAbort) {
                bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_ABORT'));
            } else if (isServicePartQuestionnaire) {
                bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_SERVICE_PART_QUESTIONNAIRE_ABORT'));
            } else {
                // If the Handover button is still disabled, then the only remaining reason is that there are un-submitted
                // participant copies
                bodyItems.push(<string>translate.instant('F4M_ACTIONBAR_INFO_ABORT_PARTICIPANT_COPIES_CREATED'));
            }
        }
    }

    // optimize usability by presenting the modal in a nice list:
    const bodyLines = bodyItems.map((i) => `<li>${i}</li>`).reduce((lines, line) => lines + line, '');
    const body = `<ul>${bodyLines}</ul>`;

    return { title, body };
}

@Component({
    selector: 'pace-forms2-navbar',
    templateUrl: './forms2-navbar.component.html',
    styleUrls: ['./forms2-navbar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Forms2NavbarComponent implements OnInit, OnDestroy {
    public modalInstance: BsModalRef;
    public helpModalInstance: BsModalRef;

    public form$: Observable<FormState>;
    public notification$: Observable<NotificationIdentifier>;

    public isSaving$: Observable<boolean>;
    public submitStatus$: Observable<FormsConstants.SUBMIT_STATUS>;

    public canSubmit$: Observable<boolean>;
    public canHandOver$: Observable<boolean>;
    public canReload$: Observable<boolean>;
    public canDelete$: Observable<boolean>;
    public canNavigateToConfirmation$: Observable<boolean>;
    public globalReadOnly$: Observable<boolean>;
    public readOnly$: Observable<boolean>;
    public participantCopyReadonly$: Observable<boolean>;
    public canAbort$: Observable<boolean>;
    public supportParticipantCopies$: Observable<boolean>;
    public systemCondition$: Observable<SystemCondition | undefined>;
    public signaturesAvailableAndSigned$: Observable<boolean>;
    public participantCopies$: Observable<FormParticipantCopies>;
    public readOnlyReason$: Observable<FormsConstants.READONLY_REASON>;
    public selectedParticipant$: Observable<ParticipantCopyId>;
    public hasParticipantCopies$: Observable<boolean>;
    public unSubmittedParticipantCopies$: Observable<ParticipantCopyId[]>;
    public displayNavigateToConfirmation: boolean;
    public isSaveButtonDisabled$: Observable<boolean>;
    public hideSubmitButtonForParticipant$: Observable<boolean>;
    public isInstallationReport$: Observable<boolean>;
    public formType$: Observable<FormsConstants.FORMTYPE>;
    public infoClass$: Observable<FormsConstants.INFOCLASS>;
    public isServicePartQuestionnaire$: Observable<boolean>;
    public isSafetyAssessmentFormFilled$: Observable<boolean>;
    public isSafetyAssessmentForm$: Observable<boolean>;

    public displayManualIndividualizationButton$: Observable<boolean>;
    public manualIndividualizationEnabled = false;
    public hideSubmitButtonforForms = false;
    public hideAuthorsFormsButtons = false;
    public enableDocumentFeedbackButton$: Observable<boolean>;
    private readonly helpTrigger$: Subject<void>;
    private readonly navigateToConfirmationTrigger$: Subject<void>;
    private readonly destroyTrigger$: Subject<void>;
    private readonly submitTrigger$: Subject<void>;
    private readonly abortTrigger$: Subject<void>;

    public readonly faSave = faSave;
    public readonly faRedo = faRedo;
    public readonly faMinusCircle = faMinusCircle;
    public readonly faQuestion = faQuestion;
    public readonly faUserFriends = faUserFriends;
    public readonly faFileUpload = faFileUpload;
    public readonly faFilter = faFilter;
    public readonly faUsers = faUsers;
    public readonly faArrowLeft = faArrowLeft;
    public readonly faArrowRight = faArrowRight;
    public readonly faTrashAlt = faTrashAlt;
    public readonly faTasks = faTasks;
    public readonly faSitemap = faSitemap;
    public readonly faFileSignature = faFileSignature;

    public readonly faCog = faCog;

    private readonly crossTabsReadonlyReasons = new Set([
        FormsConstants.READONLY_REASON.ABORTED_FROM_ANOTHER_TAB,
        FormsConstants.READONLY_REASON.HANDOVER_FROM_ANOTHER_TAB,
        FormsConstants.READONLY_REASON.SUBMITTED_FROM_ANOTHER_TAB,
    ]);

    protected readonly faEllipsisV = faEllipsisV;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly store: Store,
        private readonly modalService: BsModalService,
        private readonly translate: TranslateService,
        private readonly router: Router,
        @Inject(NotificationInstanceToken) private readonly notificationService: NotificationInterface,
        @Inject(ClientManagerInstanceToken) clientManager: ClientManagerInterface,
        private readonly formsStorageService: FormsStorageService,
        private readonly configService: ConfigService,
        private readonly deviceIdService: DeviceIdService,
        private readonly cdr: ChangeDetectorRef,
        private readonly broadcastService: BroadcastService,
    ) {
        this.displayNavigateToConfirmation = !clientManager.isPaceFormsForEvo();
        this.destroyTrigger$ = new Subject<void>();

        this.form$ = store.select(getForm);
        const safFormId = this.configService.getSafetyAssessmentForm();
        const isSafFormEnabled = this.configService.getEnableSafetyAssessmentForm();
        const oneSecond = 1000;
        this.isSaving$ = store.select(getFormIsSaving).pipe(
            concatMap((isSaving) => (isSaving ? of(isSaving) : of(isSaving).pipe(delay(oneSecond)))),
            tap(() => {
                this.cdr.markForCheck();
                this.cdr.detectChanges();
            }),
        );

        this.notification$ = store
            .select(getFormNotification)
            .pipe(
                filter((notification) => !NotificationUtilities.compare(notification, initialNotificationIdentifier)),
            );

        this.submitStatus$ = store.select(getFormSubmitStatusForCurrentParticipant);

        this.hideSubmitButtonforForms = this.configService.getHideSubmitButtonForForms();

        this.hideAuthorsFormsButtons = this.configService.getHideAuthorsFormsButtons();

        // The participant-copies button should be disabled if the form is opened in read-only mode.
        this.supportParticipantCopies$ = store.select(getFormSupportParticipantCopies);
        this.globalReadOnly$ = store.select(getFormGlobalReadonly);
        this.readOnly$ = store.select(getFormParticipantCopyReadonly);
        this.participantCopyReadonly$ = store.select(getFormParticipantCopyReadonly);
        this.readOnlyReason$ = store.select(getFormReadonlyReason);
        this.selectedParticipant$ = store.select(getFormSelectedParticipantCopyId);
        this.systemCondition$ = store.select(getFormSystemCondition);
        this.signaturesAvailableAndSigned$ = store.select(getSignaturesValid);
        this.participantCopies$ = store.select(getFormParticipantCopies);
        this.isInstallationReport$ = store.select(getInstallationForm);
        this.formType$ = store.select(getFormType);
        this.infoClass$ = store.select(getFormInfoClass);
        this.isServicePartQuestionnaire$ = this.formType$.pipe(
            map((type) => FormUtilities.isServicePartQuestionnaire(type)),
        );

        this.enableDocumentFeedbackButton$ = combineLatest([this.readOnly$, this.formType$]).pipe(
            map(([readOnly, formType]) => {
                const isAstoriaForm = FormUtilities.isAstoriaForm(formType);

                return this.configService.getEnableDocumentFeedbackButton() && !readOnly && !isAstoriaForm;
            }),
        );

        this.hasParticipantCopies$ = this.participantCopies$.pipe(
            map((participantCopies) => Object.values(participantCopies).filter((p) => p.hasCopy)),
            map((participantsWithCopies) => participantsWithCopies.length > 1),
        );
        this.isSaveButtonDisabled$ = combineLatest([
            this.readOnly$.pipe(startWith(true)),
            this.isSaving$.pipe(startWith(true)),
        ]).pipe(
            map(([readOnly, isSaving]) => {
                return readOnly || isSaving;
            }),
        );

        this.hideSubmitButtonForParticipant$ = this.selectedParticipant$.pipe(
            map(
                (selectedParticipantId) =>
                    selectedParticipantId !== FormsConstants.MASTER_PARTICIPANT_ID &&
                    this.configService.getHideParticipantFormSubmission(),
            ),
        );

        const hasBeenSubmitted$ = combineLatest([
            this.form$,
            this.notification$.pipe(
                mergeMap((notification) => this.formsStorageService.loadFormSubmitStatus(notification)),
            ),
            this.readOnlyReason$,
        ]).pipe(
            switchMap(([form, submitStatus, readOnlyReason]) =>
                this.store
                    .select(
                        getPreviousSubmitFromFormListEntry({
                            formId: form.formId,
                            notification: form.notification,
                        }),
                    )
                    .pipe(map((previousSubmit) => ({ form, submitStatus, readOnlyReason, previousSubmit }))),
            ),
            map(({ form, submitStatus, readOnlyReason, previousSubmit }) => {
                const status = submitStatus[form.formId.formId];
                return (
                    FormsConstants.readOnlySubmitStatuses.has(status) ||
                    previousSubmit ||
                    this.crossTabsReadonlyReasons.has(readOnlyReason)
                );
            }),
        );

        const currentDeviceHasLock$ = this.store.select(getDeviceId).pipe(
            mergeMap(async (deviceId) => {
                const legacyDeviceId = this.configService.getEnablePaceFormsCrossDeviceSync()
                    ? await this.deviceIdService.getLegacyDeviceIdAsync(this.configService.getUserFrontId())
                    : FormsConstants.NO_LOCK_DEVICE_ID;
                return legacyDeviceId === deviceId;
            }),
        );

        const currentUserHasLock$ = this.store
            .select(getFormLock)
            .pipe(map((lockedBy) => this.configService.getUserFrontId() === lockedBy));

        const hasLock$ = combineLatest([currentUserHasLock$, currentDeviceHasLock$]).pipe(
            map(([userHasLock, deviceHasLock]) => userHasLock && deviceHasLock),
        );

        this.canAbort$ = combineLatest([
            // Only allow abort if none of the participantCopies have been copied(the copies get created at an earlier point
            // but get the hasCopy attribute set to true, after we are generating them). If there are participant copies, no abort
            // is possible any more. If the user really wants to abort after creating participant copies, then the
            // participant copies have to be manually deleted first.
            this.participantCopies$.pipe(
                map((participantCopies) => {
                    return Object.values(participantCopies)
                        .filter((copy) => copy.id !== FormsConstants.MASTER_PARTICIPANT_ID)
                        .every((copy) => !copy.hasCopy);
                }),
            ),
            // if the form has been submitted, we we will not allow aborting since the form is in read-only mode anyway.
            hasBeenSubmitted$,
            // if the user does not have the lock, we don't allow editing
            hasLock$,
            // if it is a service part questionnaire form, then we don't allow aborting
            this.isServicePartQuestionnaire$,
        ]).pipe(
            map(([hasNoCopiesYet, hasBeenSubmitted, hasLock, isQuestionnaire]) => {
                // SHP-833 pace-eVO needs to finish all forms to continue, users should be able to abort forms not locked to themself
                return (
                    hasNoCopiesYet &&
                    !hasBeenSubmitted &&
                    !isQuestionnaire &&
                    (hasLock || clientManager.isPaceFormsForEvo())
                );
            }),
        );

        this.canSubmit$ = combineLatest([
            this.readOnly$,
            this.supportParticipantCopies$,
            this.selectedParticipant$,
            this.participantCopies$,
            this.systemCondition$,
            this.signaturesAvailableAndSigned$,
        ]).pipe(
            filter(([, , selectedParticipant, participantCopies, , ,]) => !!participantCopies[selectedParticipant]),
            map(
                ([
                    readOnly,
                    supportParticipantCopies,
                    selectedParticipant,
                    participantCopies,
                    systemCondition,
                    signaturesAvailableAndSigned,
                ]) =>
                    !readOnly &&
                    ((!isDefined(systemCondition) && signaturesAvailableAndSigned) ||
                        (isDefined(systemCondition) && SubmitStatusUtilities.systemConditionValid(systemCondition))) &&
                    !SubmitStatusUtilities.hasBeenSubmitted(participantCopies[selectedParticipant]) &&
                    (!supportParticipantCopies ||
                        (selectedParticipant === FormsConstants.MASTER_PARTICIPANT_ID &&
                            SubmitStatusUtilities.hasSignedParticipantCopies(selectedParticipant, participantCopies)) ||
                        (selectedParticipant !== FormsConstants.MASTER_PARTICIPANT_ID &&
                            SubmitStatusUtilities.isSignedParticipantCopy(participantCopies[selectedParticipant]))),
            ),
        );

        const canSubmitQuestionnaire$ = this.store.select(getQuestionnaireSubmitStatus);
        this.canSubmit$ = combineLatest([
            this.canSubmit$,
            this.isServicePartQuestionnaire$,
            canSubmitQuestionnaire$,
        ]).pipe(
            map(([canSubmit, isServicePartQuestionnaire, canSubmitQuestionnaire]) =>
                isServicePartQuestionnaire ? canSubmit && canSubmitQuestionnaire : canSubmit,
            ),
        );

        this.unSubmittedParticipantCopies$ = store.select(getFormUnSubmittedParticipantCopyIds);
        this.canHandOver$ = combineLatest([
            this.readOnly$,
            this.selectedParticipant$,
            this.unSubmittedParticipantCopies$,
            this.isServicePartQuestionnaire$,
        ]).pipe(
            map(
                ([readOnly, selectedParticipant, unSubmittedParticipantCopies, isQuestionnaire]) =>
                    selectedParticipant === FormsConstants.MASTER_PARTICIPANT_ID &&
                    !readOnly &&
                    !isQuestionnaire &&
                    unSubmittedParticipantCopies.length === 0,
            ),
        );

        this.canReload$ = combineLatest([this.selectedParticipant$, this.readOnlyReason$]).pipe(
            map(
                ([selectedParticipant, readOnlyReason]) =>
                    selectedParticipant === FormsConstants.MASTER_PARTICIPANT_ID &&
                    (readOnlyReason === FormsConstants.READONLY_REASON.LOCK ||
                        readOnlyReason === FormsConstants.READONLY_REASON.NOT_LOCKED),
            ),
        );

        this.canDelete$ = combineLatest([this.selectedParticipant$, this.participantCopies$, this.readOnly$]).pipe(
            map(
                ([selectedParticipant, participantCopies, readOnly]) =>
                    selectedParticipant !== FormsConstants.MASTER_PARTICIPANT_ID &&
                    !readOnly &&
                    participantCopies[selectedParticipant].submittedOn.length === 0,
            ),
        );

        this.canNavigateToConfirmation$ = this.notification$.pipe(
            map((notification) => {
                const status = this.notificationService.getStatusStateByIdentifier(notification);
                // Now for P58, along with OSR,SORS,TOBS we have four other status, ROSR,FCC,RTOBS and FTOBS
                return (
                    status.statusCode === NOTIFICATION_STATUS_CODE.OSR ||
                    status.statusCode === NOTIFICATION_STATUS_CODE.SOSR ||
                    status.statusCode === NOTIFICATION_STATUS_CODE.TOBS ||
                    status.statusCode === NOTIFICATION_STATUS_CODE.ROSR ||
                    status.statusCode === NOTIFICATION_STATUS_CODE.FCC ||
                    status.statusCode === NOTIFICATION_STATUS_CODE.RTOBS ||
                    status.statusCode === NOTIFICATION_STATUS_CODE.FTOBS
                );
            }),
        );

        this.isSafetyAssessmentFormFilled$ = combineLatest([this.form$, this.submitStatus$]).pipe(
            map(([form, submitStatus]) => {
                return (
                    form.formId.formId === safFormId &&
                    [FormsConstants.SUBMIT_STATUS.FINAL, FormsConstants.SUBMIT_STATUS.FINAL_NOT_OK].includes(
                        submitStatus,
                    )
                );
            }),
        );

        this.isSafetyAssessmentForm$ = this.form$.pipe(
            map((form) => {
                return form.formId.formId === safFormId && isSafFormEnabled;
            }),
        );

        this.canSubmit$ = combineLatest([this.canSubmit$, this.isSafetyAssessmentFormFilled$, this.form$]).pipe(
            map(([canSubmit, isAtsFormFilled, form]) => {
                const isSafForm = form.formId.formId === safFormId;
                const isFormSubmitted = form.participantCopies.master.submittedOn !== '';

                const shouldUseSAFStatus = isSafFormEnabled && isSafForm && !isFormSubmitted;

                return shouldUseSAFStatus ? isAtsFormFilled : canSubmit;
            }),
        );

        this.displayManualIndividualizationButton$ = combineLatest([this.formType$, this.isSafetyAssessmentForm$]).pipe(
            map(([formType, isAtsForm]) => {
                const isTrainingReport = [
                    FormsConstants.FORMTYPE.APPLICATION_TRAINING_REPORT,
                    FormsConstants.FORMTYPE.PRE_TRAINING_CLARIFICATION_REPORT,
                ].includes(formType);

                return this.configService.getEnableManualIndividualization() && !isTrainingReport && !isAtsForm;
            }),
        );

        this.navigateToConfirmationTrigger$ = new Subject<void>();
        this.navigateToConfirmationTrigger$
            .pipe(
                switchMap(() => {
                    this.log.debug('Forms2NavbarComponent: navigateToConfirmationTrigger');
                    return this.notification$;
                }),
                exhaustMap((notification) =>
                    this.router.navigate([
                        FormsConstants.ROUTE_CONFIRMATION,
                        notification.frontId,
                        notification.qmNum,
                        notification.maNum,
                        FormsConstants.CONFIRMATION_TYPE_NORMAL,
                    ]),
                ),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe();

        const helpSources$ = combineLatest([
            this.form$,
            this.notification$,
            this.readOnlyReason$,
            this.participantCopyReadonly$,
            this.submitStatus$,
            this.canHandOver$,
            this.canSubmit$,
            this.canDelete$,
            this.canAbort$,
            this.supportParticipantCopies$,
            this.systemCondition$,
            this.isInstallationReport$,
            this.isServicePartQuestionnaire$,
            currentDeviceHasLock$,
            currentUserHasLock$,
            this.isSafetyAssessmentForm$,
        ]).pipe(take(1));

        this.helpTrigger$ = new Subject<void>();
        this.helpTrigger$
            .pipe(
                switchMap(() => helpSources$),
                map(
                    (
                        helpSources: [
                            FormState,
                            NotificationIdentifier,
                            FormsConstants.READONLY_REASON,
                            boolean,
                            FormsConstants.SUBMIT_STATUS,
                            boolean,
                            boolean,
                            boolean,
                            boolean,
                            boolean,
                            SystemCondition,
                            boolean,
                            boolean,
                            boolean,
                            boolean,
                            boolean,
                        ],
                    ) => getHelpMessage(this.translate, ...helpSources),
                ),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe(({ title, body }) => {
                this.helpModalInstance = this.modalService.show(YesNoModalDialogComponent, {
                    backdrop: 'static',
                    class: ModalClass.MODAL_MEDIUM,
                    initialState: {
                        model: new YesNoModalDialogModel(title, body, 'OK'),
                    },
                    ariaLabelledBy: 'yes-no-modal-title',
                });
            });

        this.submitTrigger$ = new Subject<void>();
        this.submitTrigger$
            .asObservable()
            .pipe(
                exhaustMap(() =>
                    // 832, 834 + 837 without participant copies
                    combineLatest([
                        this.supportParticipantCopies$,
                        this.selectedParticipant$,
                        this.submitStatus$,
                        this.formType$,
                        this.infoClass$,
                    ]).pipe(
                        take(1),
                        mergeMap(
                            ([supportParticipantCopies, selectedParticipant, submitStatus, formType, infoClass]) => {
                                let msg = '';
                                let title = '';
                                let yesButton = '';
                                let noButton = '';
                                if (
                                    [
                                        FormsConstants.SUBMIT_STATUS.INTERIM,
                                        FormsConstants.SUBMIT_STATUS.INTERIM_NOT_OK,
                                    ].includes(submitStatus)
                                ) {
                                    const formTypeTranslation = this.getFormTypeTranslation(formType, infoClass);
                                    title = <string>this.translate.instant('SUBMISSION_MODAL_ATTENTION');
                                    msg = <string>this.translate.instant('SUBMISSION_MODAL_REPORT_INTERIM', {
                                        reportType: formTypeTranslation,
                                    });
                                    yesButton = <string>this.translate.instant('F4M_SUMMARY_MODAL_SUBMIT_LABEL');
                                    // TODO: Change the label of the button, when the "open topics" filter
                                    // noButton = <string>this.translate.instant('SUBMISSION_MODAL_BACK_TO_OPEN_TOPICS');
                                    noButton = <string>this.translate.instant('SUBMISSION_MODAL_BACK_TO_FORM');
                                } else if (
                                    [
                                        FormsConstants.SUBMIT_STATUS.FINAL,
                                        FormsConstants.SUBMIT_STATUS.FINAL_NOT_OK,
                                    ].includes(submitStatus)
                                ) {
                                    title = <string>this.translate.instant('F4M_SUBMIT_FORM');
                                    msg = <string>this.translate.instant('F4M_NAVBAR_SUBMIT_FORM_QUESTION');
                                    yesButton = <string>this.translate.instant('F4M_YES_BUTTON_LABEL');
                                    noButton = <string>this.translate.instant('F4M_NO_BUTTON_LABEL');
                                }

                                const modalInstance = this.modalService.show(YesNoModalDialogComponent, {
                                    backdrop: 'static',
                                    class: ModalClass.MODAL_MEDIUM,
                                    initialState: {
                                        model: new YesNoModalDialogModel(title, msg, yesButton, noButton),
                                    },
                                    ariaLabelledBy: 'yes-no-modal-title',
                                });
                                return (
                                    modalInstance.content?.modalResult.pipe(
                                        map((submit) => ({
                                            submit,
                                            selectedParticipant,
                                            submitStatus,
                                            supportParticipantCopies,
                                        })),
                                    ) ??
                                    of({
                                        submit: false,
                                        selectedParticipant,
                                        submitStatus,
                                        supportParticipantCopies,
                                    })
                                );
                            },
                        ),
                        mergeMap(({ submit, selectedParticipant, submitStatus, supportParticipantCopies }) => {
                            if (
                                [
                                    FormsConstants.SUBMIT_STATUS.INTERIM,
                                    FormsConstants.SUBMIT_STATUS.INTERIM_NOT_OK,
                                ].includes(submitStatus)
                            ) {
                                if (!submit) {
                                    // Commented out, because of the ticket https://evorait.atlassian.net/browse/SHP-5925
                                    // TODO: to be implemented after the requirements of the open topics filter have been refined
                                    /* this.store.dispatch(
                                        updateFormFilter({
                                            filterType: FormFiltersType.INCOMPLETE_ELEMENTS,
                                            visible: true,
                                        })
                                    ); */
                                } else if (submit && supportParticipantCopies) {
                                    const modalInstance = this.modalService.show(YesNoModalDialogComponent, {
                                        backdrop: 'static',
                                        class: ModalClass.MODAL_MEDIUM,
                                        initialState: {
                                            model: new YesNoModalDialogModel(
                                                <string>this.translate.instant('F4M_SUBMIT_FORM'),
                                                <string>(
                                                    this.translate.instant(
                                                        'F4M_NAVBAR_SUBMIT_MASTER_FORM_AND_UNSUBMITTED_PARTICIPANTS_QUESTION',
                                                    )
                                                ),
                                                <string>this.translate.instant('F4M_YES_BUTTON_LABEL'),
                                                <string>this.translate.instant('F4M_NO_BUTTON_LABEL'),
                                            ),
                                        },
                                        ariaLabelledBy: 'yes-no-modal-title',
                                    });
                                    if (modalInstance.content) {
                                        return modalInstance.content.modalResult.pipe(
                                            map((value) => {
                                                return {
                                                    submit: value,
                                                    selectedParticipant,
                                                    submitStatus,
                                                };
                                            }),
                                        );
                                    }
                                }
                            }

                            return of({
                                submit,
                                selectedParticipant,
                                submitStatus,
                            });
                        }),
                    ),
                ),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe(({ submit, selectedParticipant, submitStatus }) => {
                // submit directly if the user opted so
                if (submit) {
                    this.store.dispatch(submitForm({ id: selectedParticipant, saveMode: submitStatus }));
                }
            });

        this.abortTrigger$ = new Subject<void>();
        this.abortTrigger$
            .asObservable()
            .pipe(
                exhaustMap(() =>
                    this.readOnly$.pipe(
                        take(1),
                        mergeMap((readOnly) => {
                            const bodyText = readOnly
                                ? <string>this.translate.instant('F4M_ABORT_CONFIRMATION_READONLY_BODY')
                                : <string>this.translate.instant('F4M_ABORT_CONFIRMATION_BODY');
                            const modalInstance = this.modalService.show(YesNoModalDialogComponent, {
                                backdrop: 'static',
                                class: ModalClass.MODAL_MEDIUM,
                                initialState: {
                                    model: new YesNoModalDialogModel(
                                        <string>this.translate.instant('F4M_ABORT_CONFIRMATION_TITLE'),
                                        bodyText,
                                        <string>this.translate.instant('F4M_ABORT_CONFIRMATION_YES'),
                                        <string>this.translate.instant('F4M_ABORT_CONFIRMATION_NO'),
                                    ),
                                },
                                ariaLabelledBy: 'yes-no-modal-title',
                            });
                            return modalInstance.content?.modalResult ?? of(undefined);
                        }),
                        filter((result) => !!result),
                        withLatestFrom(this.form$, this.notification$),
                    ),
                ),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe(([, form, notification]) => {
                this.store.dispatch(abortForm({ form: form.formId, formType: form.type, notification }));
            });
    }

    private getFormTypeTranslation(formType: FormsConstants.FORMTYPE, infoClass: FormsConstants.INFOCLASS): string {
        // astoria uses 837 for training and installation, this is only for text, so we just hard set it to INSTALLATION_REPORT
        const adaptedFormType =
            formType === FormsConstants.FORMTYPE.TRAININGCHECKLIST &&
            infoClass === FormsConstants.INFOCLASS.INSTALLATION
                ? FormsConstants.FORMTYPE.INSTALLATION_REPORT
                : formType;
        const formTypeKey = Object.keys(FormsConstants.FORMTYPE)[
            Object.values(FormsConstants.FORMTYPE).indexOf(adaptedFormType)
        ];
        return <string>this.translate.instant(`F4M_FORM_TYPE_${formTypeKey}`);
    }

    public ngOnInit(): void {
        // when form is aborted/handover/submitted form 1st tab, form on tab 2 will be readonly
        this.broadcastService.message$
            .pipe(
                withLatestFrom(this.form$),
                filter(
                    ([msg, form]) =>
                        msg.formId === form.formId.formId && msg.notificationNumber === form.notification.qmNum,
                ),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe(([msg, form]) => {
                if (msg.saveStatus === FormsConstants.SUBMIT_STATUS.EDITED) {
                    const formType = FormUtilities.getFormTypeFromId(form.formId.formId);
                    return this.store.dispatch(
                        loadForm({
                            form: form.formId,
                            notification: form.notification,
                            status: FormsConstants.FORMSTATUS.LOCK,
                            formType,
                            language: form.language,
                        }),
                    );
                } else {
                    const readonlyReason = this.getReadOnlyReasonByMessage(msg);
                    this.store.dispatch(updateFormReadOnlyReason({ readonlyReason }));
                }
            });

        // setup event handler for auto-save on suspend
        fromEvent(document, 'pause')
            .pipe(
                switchMap(() => this.readOnly$),
                filter((readOnly) => !readOnly),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe(() => {
                this.log.debug(`Forms2Component: pause event: form is editable => dispatching save action`);
                this.store.dispatch(saveForm());
            });

        this.store
            .pipe(
                select(getManualIndividualizationStatus),
                withLatestFrom(this.store.pipe(select(getAccordionGroups))),
                tap(([, groups]) => {
                    for (const key of Object.keys(groups)) {
                        if (groups[key] && key.includes('.')) {
                            this.store.dispatch(collapseAccordionByGroup({ groupId: key }));
                        }
                    }
                }),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe();
    }

    public ngOnDestroy(): void {
        this.destroyTrigger$.next();
    }

    public selectPreviousParticipant(event: Event): void {
        if (event) {
            event.stopPropagation();
        }
        this.store.dispatch(selectPreviousParticipant());
    }

    public selectNextParticipant(event: Event): void {
        if (event) {
            event.stopPropagation();
        }
        this.store.dispatch(selectNextParticipant());
    }

    public help(): void {
        this.helpTrigger$.next();
    }

    public navigateToConfirmation(): void {
        this.navigateToConfirmationTrigger$.next();
    }

    public refresh(): void {
        const modalInstance = this.modalService.show(YesNoModalDialogComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: new YesNoModalDialogModel(
                    <string>this.translate.instant('F4M_RELOAD_FORM_TITLE'),
                    <string>this.translate.instant('F4M_RELOAD_FORM_MESSAGE_BODY'),
                    <string>this.translate.instant('F4M_YES_BUTTON_LABEL'),
                    <string>this.translate.instant('F4M_NO_BUTTON_LABEL'),
                ),
            },
            ariaLabelledBy: 'yes-no-modal-title',
        });
        modalInstance.content?.modalResult.subscribe((result) => {
            if (result) {
                this.ngOnInit();
            }
        });
    }

    public save(): void {
        this.log.debug(`Forms2Component: save(): Dispatching action!`);
        this.store.dispatch(saveForm());
    }

    public submit(): void {
        const isAnyFilterActive$ = this.store.select(getIsAnyFilterActive);

        isAnyFilterActive$
            .pipe(
                takeUntil(this.submitTrigger$),
                switchMap((isActive) => {
                    if (isActive) {
                        this.store.dispatch(clearFilters());
                    }
                    return of(isActive);
                }),
                filter((isActive) => !isActive),
                tap(() => this.submitTrigger$.next()),
            )
            .subscribe();
    }

    public abort(): void {
        this.abortTrigger$.next();
    }

    public handOver(): void {
        const explanation = this.configService.getEnableUnlimitedHandover()
            ? 'F4M_HAND_OVER_FORM'
            : 'F4M_HAND_OVER_FORM_T0_COLLEAGUE';

        const modalInstance = this.modalService.show(YesNoModalDialogComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: new YesNoModalDialogModel(
                    <string>this.translate.instant('F4M_ACTIONBAR_HANDOVER_LABEL'),
                    <string>this.translate.instant(explanation),
                    <string>this.translate.instant('F4M_YES_BUTTON_LABEL'),
                    <string>this.translate.instant('F4M_NO_BUTTON_LABEL'),
                ),
            },
            ariaLabelledBy: 'yes-no-modal-title',
        });
        modalInstance.content?.modalResult
            .pipe(
                take(1),
                filter((result) => result),
                map(() => FormsConstants.SUBMIT_STATUS.HANDOVER),
                withLatestFrom(this.selectedParticipant$),
            )
            .subscribe(([saveMode, participantCopyId]) => {
                this.log.debug(`Forms2Component: handOver(): Dispatching action with save mode '${saveMode}'!`);
                this.store.dispatch(submitForm({ id: participantCopyId, saveMode }));
            });
    }

    public openParticipantsModal(): void {
        this.modalService.show(ParticipantCopiesComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            ariaLabelledBy: 'participant-copies-modal-title',
        });
    }

    public openRecipientsModal(): void {
        this.modalService.show(RecipientsComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            ariaLabelledBy: 'recipients-modal-title',
        });
    }

    public deleteParticipantCopy(event: Event): void {
        if (event) {
            event.stopPropagation();
        }
        const modalInstance = this.modalService.show(YesNoModalDialogComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: new YesNoModalDialogModel(
                    <string>this.translate.instant('F4M_DELETE_PARTICIPANT_COPY'),
                    <string>this.translate.instant('F4M_NAVBAR_DELETE_PARTICIPANT_COPY'),
                    <string>this.translate.instant('F4M_YES_BUTTON_LABEL'),
                    <string>this.translate.instant('F4M_NO_BUTTON_LABEL'),
                ),
            },
            ariaLabelledBy: 'yes-no-modal-title',
        });
        modalInstance.content?.modalResult
            .pipe(
                filter((result) => result),
                withLatestFrom(this.selectedParticipant$),
                take(1),
            )
            .subscribe(([, selectedParticipant]) => {
                this.store.dispatch(removeParticipantForm({ participant: selectedParticipant }));
            });
    }

    public enableManualIndividualization(event: Event): void {
        if (event) {
            event.stopPropagation();
        }
        this.manualIndividualizationEnabled = !this.manualIndividualizationEnabled;
        this.store.dispatch(enableManualIndividualization());
    }

    public openDocumentFeedbackModal(): void {
        this.form$.pipe(first(), takeUntil(this.destroyTrigger$)).subscribe((form) => {
            const initialState = {
                model: new DocumentFeedbackModel(form),
            };

            this.modalService.show(DocumentFeedbackComponent, {
                backdrop: 'static',
                class: ModalClass.MODAL_MEDIUM,
                initialState,
            });
        });
    }

    private getReadOnlyReasonByMessage(message: FormStatusMessage): FormsConstants.READONLY_REASON {
        const statusReasonMap: Partial<Record<FormsConstants.SUBMIT_STATUS, FormsConstants.READONLY_REASON>> = {
            [FormsConstants.SUBMIT_STATUS.ABORTED]: FormsConstants.READONLY_REASON.ABORTED_FROM_ANOTHER_TAB,
            [FormsConstants.SUBMIT_STATUS.HANDOVER]: FormsConstants.READONLY_REASON.HANDOVER_FROM_ANOTHER_TAB,
            [FormsConstants.SUBMIT_STATUS.INTERIM]: FormsConstants.READONLY_REASON.SUBMITTED_FROM_ANOTHER_TAB,
            [FormsConstants.SUBMIT_STATUS.INTERIM_NOT_OK]: FormsConstants.READONLY_REASON.SUBMITTED_FROM_ANOTHER_TAB,
            [FormsConstants.SUBMIT_STATUS.FINAL]: FormsConstants.READONLY_REASON.SUBMITTED_FROM_ANOTHER_TAB,
            [FormsConstants.SUBMIT_STATUS.FINAL_NOT_OK]: FormsConstants.READONLY_REASON.SUBMITTED_FROM_ANOTHER_TAB,
        };

        return statusReasonMap[message.saveStatus] ?? FormsConstants.READONLY_REASON.NONE;
    }
}
